import { ICourseProduct, IMembership } from '@/models/Learning'

import { MembershipType } from '@/models/Groups'
import { CoursesService } from '@/service/CoursesService'
import { reactive } from 'vue'
import { GroupState } from './groups'

export class learningState {
  state = reactive({
    groupCourses: new Map<string, ICourseProduct[]>(),
    userCourses: new Map<string, ICourseProduct[]>(), //Map<groupId, ICourseProduct[]> we are using this map to store the user's courses for every group they are part of
    availableCourses: [] as ICourseProduct[],
    coursesMap: new Map<string, ICourseProduct>(),
  })

  async fetchLocationCourses(): Promise<void> {
    if (this.state.availableCourses.length > 0) {
      return
    }
    const response = await CoursesService.getAllLocationCourses()
    this.state.availableCourses.push(...response)
    this.state.availableCourses.sort((a, b) => {
      return a.title.localeCompare(b.title)
    })
  }

  async fetchGroupCourses(): Promise<void> {
    if (this.state.groupCourses.has(GroupState.state.groupData._id)) {
      return
    }
    const response = await CoursesService.getGroupCourses({
      limit: 10,
      pageNo: 0,
    })

    this.state.groupCourses.set(GroupState.state.groupData._id, [])
    const courses = [] as ICourseProduct[]
    response.forEach(courseProduct => {
      courses.push(courseProduct)
    })
    this.state.groupCourses.set(GroupState.state.groupData._id, courses)
  }

  async fetchGroupCourse(productId: string): Promise<ICourseProduct> {
    return await CoursesService.getGroupCourse(productId)
  }

  async initializeLearningState(): Promise<void> {
    if (GroupState.state.isGroupAdmin || GroupState.state.role === null) {
      this.fetchGroupCourses()
    } else {
      this.fetchUserCourses()
    }
  }

  async fetchUserCourses(): Promise<void> {
    if (this.state.userCourses.has(GroupState.state.groupData._id)) {
      return
    }
    const response = await CoursesService.getUserCourses({
      limit: 10,
      pageNo: 0,
    })
    this.state.userCourses.set(GroupState.state.groupData._id, [])
    const courses = [] as ICourseProduct[]
    response.forEach(courseProduct => {
      // store purchaseId in course object
      courseProduct.purchaseId = courseProduct?.product[0]?.purchaseId

      courses.push(courseProduct)
    })
    this.state.userCourses.set(GroupState.state.groupData._id, courses)
  }

  async addCourseToGroup(
    productId: string,
    productName: string,
    membership?: IMembership
  ): Promise<void> {
    const response = await CoursesService.addCourseToGroup(
      productId,
      productName,
      membership
    )
    const groupCourses = this.state.groupCourses.get(
      GroupState.state.groupData._id
    )

    if (groupCourses) {
      groupCourses.unshift(response)
    } else {
      this.state.groupCourses.set(GroupState.state.groupData._id, [response])
    }
  }

  async updateCourseFromGroup(
    offerId: string,
    productName: string,
    membership?: IMembership
  ): Promise<void> {
    if (membership?.membershipType === MembershipType.PAID) {
      membership.price = Number(membership.price)
      membership.trialDays = Number(membership.trialDays)
      membership.isLivePayment = Boolean(membership.isLivePayment)
    }

    const response = await CoursesService.updateCourseFromGroup(
      offerId,
      productName,
      membership
    )
    const groupCourses = this.state.groupCourses.get(
      GroupState.state.groupData._id
    )
    if (groupCourses) {
      const courseProduct = groupCourses.map(courseProduct => {
        if (courseProduct.offerId === offerId) {
          courseProduct.membership = response.membership
        }
        return courseProduct
      })

      this.state.groupCourses.set(GroupState.state.groupData._id, courseProduct)
    } else {
      this.state.groupCourses.set(GroupState.state.groupData._id, [response])
    }
  }

  async purchaseCourse(offerId: string): Promise<any> {
    // wait for CoursesService to be defined
    if (CoursesService === undefined) {
      await new Promise(resolve => {
        const interval = setInterval(() => {
          if (CoursesService) {
            clearInterval(interval)
            resolve(true)
          }
        }, 100)
      })
    }
    const response = await CoursesService.purchaseCourse(offerId)
    const userCourses = this.state.userCourses.get(
      GroupState.state.groupData._id
    )
    //find and update the course to userGroupCourses map

    if (userCourses) {
      const courseProduct = userCourses.map(courseProduct => {
        if (courseProduct.offerId === offerId) {
          courseProduct.product = [{ ...response, purchaseId: response?.id }]
          courseProduct.purchaseId = response?.id
        }
        return courseProduct
      })
      this.state.userCourses.set(GroupState.state.groupData._id, courseProduct)
    }
    return response
  }

  async deleteCourseFromGroup(productId: string): Promise<void> {
    // remove the course from the groupCourses map
    const groupCourses = this.state.groupCourses.get(
      GroupState.state.groupData._id
    )
    if (groupCourses) {
      const updatedGroupCourses = groupCourses.filter(
        courseProduct => courseProduct.id !== productId
      )
      this.state.groupCourses.set(
        GroupState.state.groupData._id,
        updatedGroupCourses
      )
    }

    //remove the course from userGroupCourses map
    const userGroupCourses = this.state.userCourses.get(
      GroupState.state.groupData._id
    )
    if (userGroupCourses) {
      const updatedUserGroupCourses = userGroupCourses.filter(
        courseProduct => courseProduct.id !== productId
      )
      this.state.userCourses.set(
        GroupState.state.groupData._id,
        updatedUserGroupCourses
      )
    }

    await CoursesService.deleteCourseFromGroup(productId as string)
  }
  async rearrangeCourse(oldIndex: number, newIndex: number): Promise<void> {
    const groupCourses = this.state.groupCourses.get(
      GroupState.state.groupData._id
    )
    // rearrange the course in the groupCourses map
    if (groupCourses) {
      const courseProduct = groupCourses[oldIndex]
      groupCourses.splice(oldIndex, 1)
      groupCourses.splice(newIndex, 0, courseProduct)
    }
    if (groupCourses && groupCourses.length > 1) {
      // fetch previous course id and next course id
      try {
        const previousIndex = newIndex - 1
        const nextIndex = newIndex + 1
        await CoursesService.rearrangeCourses(
          groupCourses.at(newIndex)?.id as string,
          previousIndex >= 0 ? groupCourses.at(previousIndex)?.id : undefined,
          nextIndex <= groupCourses.length
            ? groupCourses.at(nextIndex)?.id
            : undefined
        )
      } catch (e) {
        console.error(e)
      }
    }
  }

  async fetchCourseById(courseId: string): Promise<ICourseProduct | string> {
    try {
      if (this.state.coursesMap[courseId]) {
        return this.state.coursesMap[courseId]
      }
      const response = await CoursesService.getCourseById(courseId)

      this.state.coursesMap[courseId] = response
      return response
    } catch (error: any) {
      if (error?.response?.status === 404) {
        this.state.coursesMap[courseId] = 'deleted'
        return 'deleted'
      } else {
        this.state.coursesMap[courseId] = 'not-accessible'
        return 'not-accessible'
      }
    }
  }

  resetState(): void {
    this.state.availableCourses = []
  }
}

// currently this function is unsed but will be used in future when courses APIs changes
// function fixImageUrl(imageUrl?: string): string | undefined {
//   if (imageUrl && !imageUrl.includes('http')) {
//     return MEMBERSHIP_ASSETS_BUCKET + imageUrl
//   }
//   return imageUrl
// }

export const LearningState = new learningState()
